import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, X, AlertCircle, ImageIcon, Filter, Video } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { API } from '../utils/api';

const CATEGORIES = [
  { value: 'pates', label: 'Pâtes fraîches', color: 'green' },
  { value: 'fromages', label: 'Fromages', color: 'yellow' },
  { value: 'charcuteries', label: 'Charcuteries', color: 'red' },
  { value: 'plats', label: 'Plats cuisinés', color: 'orange' },
  { value: 'desserts', label: 'Desserts', color: 'pink' }
];

const ProductsAdmin = () => {
  const { toast } = useToast();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    image: '',
    images: [],
    video: '',
    link_url: '',
    link_text: '',
    category: 'pates',
    cookingTimeOrAdvice: ''
  });
  const [errors, setErrors] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadingMultiple, setUploadingMultiple] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingProduct, setViewingProduct] = useState(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les produits",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Type de fichier non autorisé",
        description: "Utilisez JPG, PNG, GIF ou WEBP",
        variant: "destructive"
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "Fichier trop volumineux",
        description: "La taille maximum est de 10MB",
        variant: "destructive"
      });
      return;
    }

    setUploading(true);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/upload`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setFormData(prev => ({ ...prev, image: response.data.url }));
      setErrors(prev => ({ ...prev, image: '' }));
      
      toast({
        title: "✅ Succès",
        description: "Image uploadée avec succès !"
      });
    } catch (error) {
      console.error('Error uploading file:', error);
      toast({
        title: "❌ Erreur d'upload",
        description: error.response?.data?.detail || "Impossible d'uploader l'image",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleMultipleFilesSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    if (files.length > 10) {
      toast({ title: "Trop d'images", description: "Maximum 10 images", variant: "destructive" });
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    for (const file of files) {
      if (!allowedTypes.includes(file.type) || file.size > 10 * 1024 * 1024) {
        toast({ title: "Fichier invalide", description: `${file.name}`, variant: "destructive" });
        return;
      }
    }

    setUploadingMultiple(true);
    try {
      const formDataUpload = new FormData();
      files.forEach(file => formDataUpload.append('files', file));
      const response = await axios.post(`${API}/upload-multiple`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFormData(prev => ({ ...prev, images: [...prev.images, ...response.data.urls] }));
      toast({ title: "✅ Succès", description: `${response.data.count} image(s) uploadée(s) !` });
    } catch (error) {
      toast({ title: "❌ Erreur", description: "Impossible d'uploader", variant: "destructive" });
    } finally {
      setUploadingMultiple(false);
    }
  };

  const removeGalleryImage = (indexToRemove) => {
    setFormData(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== indexToRemove) }));
  };

  // Upload vidéo
  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'video/x-msvideo', 'video/mpeg'];
    if (!allowedTypes.includes(file.type)) {
      toast({
        title: "Type de fichier non autorisé",
        description: "Utilisez MP4, WebM, MOV, AVI ou MPEG",
        variant: "destructive"
      });
      return;
    }

    if (file.size > 100 * 1024 * 1024) {
      toast({
        title: "Fichier trop volumineux",
        description: "La taille maximum est de 100MB",
        variant: "destructive"
      });
      return;
    }

    setUploadingVideo(true);

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API}/upload-video`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setFormData(prev => ({ ...prev, video: response.data.url }));
      toast({ title: "✅ Succès", description: "Vidéo uploadée avec succès !" });
    } catch (error) {
      toast({
        title: "❌ Erreur",
        description: error.response?.data?.detail || "Impossible d'uploader la vidéo",
        variant: "destructive"
      });
    } finally {
      setUploadingVideo(false);
    }
  };

  const removeVideo = () => {
    setFormData(prev => ({ ...prev, video: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = "Le titre est requis";
    if (!formData.description.trim()) newErrors.description = "La description est requise";
    // Image OU vidéo requis
    if (!formData.image.trim() && !formData.video.trim()) {
      newErrors.media = "Veuillez ajouter au moins une image ou une vidéo";
    }
    if (!formData.cookingTimeOrAdvice.trim()) newErrors.cookingTimeOrAdvice = "Le temps de cuisson ou conseil est requis";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        title: "Erreur de validation",
        description: "Veuillez remplir tous les champs requis",
        variant: "destructive"
      });
      return;
    }

    try {
      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, formData);
        toast({
          title: "✅ Succès",
          description: "Produit mis à jour avec succès"
        });
      } else {
        const response = await axios.post(`${API}/products`, formData);
        console.log('Product created:', response.data);
        toast({
          title: "✅ Succès",
          description: "Produit créé avec succès ! La liste se met à jour..."
        });
      }

      // Close modal first
      closeModal();
      
      // Wait a bit then refresh
      setTimeout(() => {
        fetchProducts();
      }, 500);
      
    } catch (error) {
      console.error('Error saving product:', error);
      toast({
        title: "❌ Erreur",
        description: error.response?.data?.detail || "Impossible de sauvegarder le produit",
        variant: "destructive"
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce produit ?')) {
      return;
    }

    try {
      await axios.delete(`${API}/products/${id}`);
      toast({
        title: "Succès",
        description: "Produit supprimé avec succès"
      });
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast({
        title: "Erreur",
        description: "Impossible de supprimer le produit",
        variant: "destructive"
      });
    }
  };

  const openModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        title: product.title,
        description: product.description,
        image: product.image,
        images: product.images || [],
        video: product.video || '',
        link_url: product.link_url || '',
        link_text: product.link_text || '',
        category: product.category,
        cookingTimeOrAdvice: product.cookingTimeOrAdvice
      });
    } else {
      setEditingProduct(null);
      setFormData({
        title: '',
        description: '',
        image: '',
        images: [],
        video: '',
        link_url: '',
        link_text: '',
        category: 'pates',
        cookingTimeOrAdvice: ''
      });
    }
    setErrors({});
    setSelectedFiles([]);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingProduct(null);
    setFormData({
      title: '',
      description: '',
      image: '',
      images: [],
      video: '',
      link_url: '',
      link_text: '',
      category: 'pates',
      cookingTimeOrAdvice: ''
    });
    setErrors({});
    setUploading(false);
    setSelectedFiles([]);
  };

  const getCategoryInfo = (categoryValue) => {
    return CATEGORIES.find(cat => cat.value === categoryValue) || CATEGORIES[0];
  };

  const filteredProducts = filterCategory === 'all' 
    ? products 
    : products.filter(p => p.category === filterCategory);

  return (
    <div className="py-8">
      {/* Header with Filter */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <h2 className="heading-lg text-gray-900 mb-2">Gestion des Produits</h2>
          <p className="text-body text-gray-600">Gérez les produits affichés sur les pages catégories</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-gray-600" />
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
            >
              <option value="all">Toutes les catégories</option>
              {CATEGORIES.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>
          <button
            onClick={() => openModal()}
            className="btn btn-primary flex items-center gap-2 justify-center"
          >
            <Plus className="w-5 h-5" />
            Nouveau produit
          </button>
        </div>
      </div>

      {/* Products Grid */}
      {loading ? (
        <div className="text-center py-12">
          <p className="text-gray-600">Chargement des produits...</p>
        </div>
      ) : filteredProducts.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">
            {filterCategory === 'all' 
              ? 'Aucun produit pour le moment' 
              : `Aucun produit dans la catégorie "${getCategoryInfo(filterCategory).label}"`
            }
          </p>
          <button
            onClick={() => openModal()}
            className="btn btn-primary"
          >
            Créer votre premier produit
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map((product) => {
            const categoryInfo = getCategoryInfo(product.category);
            return (
              <div key={product.id} className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow">
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.title}
                    className="w-full h-full object-cover"
                  />
                  <div className={`absolute top-3 left-3 bg-${categoryInfo.color}-500 text-white px-3 py-1 rounded-full text-sm font-semibold`}>
                    {categoryInfo.label}
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="heading-sm text-gray-900 mb-2 line-clamp-2">{product.title}</h3>
                  <p className="text-body text-gray-600 line-clamp-3 mb-4">{product.description}</p>
                  <div className={`text-sm font-medium text-${categoryInfo.color}-700 bg-${categoryInfo.color}-50 px-3 py-2 rounded-lg mb-4`}>
                    {product.cookingTimeOrAdvice}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setViewingProduct(product);
                        setShowViewModal(true);
                      }}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                      title="Voir les détails"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                        <circle cx="12" cy="12" r="3"></circle>
                      </svg>
                    </button>
                    <button
                      onClick={() => openModal(product)}
                      className="flex-1 p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <Pencil className="w-4 h-4" />
                      Modifier
                    </button>
                    <button
                      onClick={() => handleDelete(product.id)}
                      className="flex-1 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      Supprimer
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="heading-lg text-gray-900">
                {editingProduct ? 'Modifier le produit' : 'Nouveau produit'}
              </h2>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Titre <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    errors.title ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ex: RAVIOLI CHAMPIGNONS"
                />
                {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="4"
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    errors.description ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Décrivez votre produit..."
                />
                {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
              </div>

              {/* Image Upload */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Image <span className="text-gray-400 text-xs">(optionnel si vidéo)</span>
                </label>
                
                <div className="mb-3">
                  <label 
                    htmlFor="file-upload-product" 
                    className={`cursor-pointer inline-flex items-center gap-2 px-6 py-3 border-2 border-dashed rounded-lg transition-colors ${
                      uploading ? 'bg-gray-100 cursor-wait border-gray-300' : formData.image ? 'border-green-500 bg-green-50' : 'border-green-500 hover:bg-green-50'
                    }`}
                  >
                    <ImageIcon className={`w-5 h-5 ${formData.image && !uploading ? 'text-green-700' : 'text-green-600'}`} />
                    <span className={`text-sm font-medium ${formData.image && !uploading ? 'text-green-700' : 'text-gray-700'}`}>
                      {uploading ? '⏳ Upload en cours...' : formData.image ? '✅ Image chargée - Cliquer pour changer' : '📁 Choisir une image depuis le PC'}
                    </span>
                  </label>
                  <input
                    id="file-upload-product"
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onChange={handleFileSelect}
                    disabled={uploading}
                    className="hidden"
                  />
                  <p className="text-xs text-gray-500 mt-1">JPG, PNG, GIF ou WEBP (max 5MB)</p>
                </div>

                {!formData.image && (
                  <div className="mb-3">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex-1 border-t border-gray-300"></div>
                      <span className="text-xs text-gray-500">OU</span>
                      <div className="flex-1 border-t border-gray-300"></div>
                    </div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Entrer une URL d'image externe
                    </label>
                    <input
                      type="url"
                      value={formData.image}
                      onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="https://example.com/image.jpg"
                      disabled={uploading}
                    />
                  </div>
                )}
                
                {errors.media && !formData.image && !formData.video && <p className="text-red-500 text-sm mt-1">{errors.media}</p>}

                {formData.image && (
                  <div className="mt-3">
                    <div className="flex justify-between items-center mb-2">
                      <p className="text-xs font-medium text-green-700">✅ Image sélectionnée</p>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, image: '' })}
                        className="text-xs text-red-600 hover:text-red-700 underline"
                      >
                        Supprimer
                      </button>
                    </div>
                    <img
                      src={formData.image}
                      alt="Aperçu"
                      className="w-full h-48 object-cover rounded-lg shadow-sm border-2 border-green-200"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Galerie d'images (Upload multiple) */}
              <div className="bg-purple-50 p-4 rounded-lg">
                <label className="block text-sm font-semibold text-purple-900 mb-3">
                  Galerie d'images <span className="text-gray-500 font-normal text-xs">(optionnel - max 10)</span>
                </label>
                <p className="text-xs text-gray-600 mb-3">Ajoutez plusieurs images pour créer un carrousel dans le modal</p>

                <label
                  htmlFor="multiple-files-upload-product"
                  className={`cursor-pointer inline-flex items-center gap-2 px-5 py-2.5 border-2 border-dashed rounded-lg transition-colors ${
                    uploadingMultiple ? 'bg-gray-100 cursor-wait border-gray-300' : 'border-purple-400 hover:bg-purple-100'
                  }`}
                >
                  <ImageIcon className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-medium text-gray-700">
                    {uploadingMultiple
                      ? 'Upload en cours...'
                      : formData.images.length > 0
                        ? `${formData.images.length} image(s) — Ajouter plus`
                        : 'Choisir plusieurs images'}
                  </span>
                </label>
                <input
                  id="multiple-files-upload-product"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                  onChange={handleMultipleFilesSelect}
                  disabled={uploadingMultiple}
                  multiple
                  className="hidden"
                />

                {formData.images.length > 0 && (
                  <div className="mt-4 grid grid-cols-3 gap-3">
                    {formData.images.map((imageUrl, index) => (
                      <div key={index} className="relative group">
                        <img
                          src={imageUrl}
                          alt={`Galerie ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg border-2 border-purple-200"
                        />
                        <button
                          type="button"
                          onClick={() => removeGalleryImage(index)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Catégorie <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              {/* Cooking Time or Advice */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Temps de cuisson ou conseil de dégustation <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.cookingTimeOrAdvice}
                  onChange={(e) => setFormData({ ...formData, cookingTimeOrAdvice: e.target.value })}
                  className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent ${
                    errors.cookingTimeOrAdvice ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="Ex: Cuisson : 7-10 min OU Conseil : À servir frais"
                />
                {errors.cookingTimeOrAdvice && <p className="text-red-500 text-sm mt-1">{errors.cookingTimeOrAdvice}</p>}
              </div>

              {/* Vidéo (optionnel) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Video className="w-4 h-4 inline mr-2" />
                  Vidéo (optionnel)
                </label>
                
                {formData.video ? (
                  <div className="space-y-3">
                    <div className="relative">
                      <video 
                        src={formData.video} 
                        controls 
                        className="w-full h-48 object-cover rounded-lg bg-black"
                      />
                      <button
                        type="button"
                        onClick={removeVideo}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-sm text-green-600">✓ Vidéo ajoutée</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <label className={`flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-green-500 hover:bg-green-50 transition-colors ${uploadingVideo ? 'opacity-50 cursor-not-allowed' : ''}`}>
                      <input
                        type="file"
                        accept="video/mp4,video/webm,video/quicktime,video/x-msvideo,video/mpeg"
                        onChange={handleVideoUpload}
                        className="hidden"
                        disabled={uploadingVideo}
                      />
                      <Video className="w-5 h-5 text-gray-400" />
                      <span className="text-sm text-gray-600">
                        {uploadingVideo ? 'Upload en cours...' : 'Choisir une vidéo (max 100MB)'}
                      </span>
                    </label>
                    <div className="text-center text-gray-400 text-sm">ou</div>
                    <input
                      type="url"
                      value={formData.video}
                      onChange={(e) => setFormData({ ...formData, video: e.target.value })}
                      placeholder="Coller l'URL d'une vidéo YouTube ou autre"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500">Formats: MP4, WebM, MOV, AVI, MPEG. Ou collez un lien YouTube.</p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  {editingProduct ? 'Mettre à jour' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de visualisation plein écran */}
      {showViewModal && viewingProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-[100000] p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
              <h3 className="text-2xl font-bold text-gray-900">Détails du produit</h3>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setViewingProduct(null);
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-8 h-8" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Image en grand */}
              {viewingProduct.image && (
                <div className="w-full rounded-lg overflow-hidden">
                  <img
                    src={viewingProduct.image}
                    alt={viewingProduct.title}
                    className="w-full h-auto max-h-[500px] object-contain bg-gray-50"
                  />
                </div>
              )}

              {/* Titre */}
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">TITRE</h4>
                <p className="text-2xl font-bold text-gray-900">{viewingProduct.title}</p>
              </div>

              {/* Description complète */}
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-2">DESCRIPTION</h4>
                <p className="text-lg text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {viewingProduct.description}
                </p>
              </div>

              {/* Informations supplémentaires */}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">CATÉGORIE</h4>
                  <span className={`inline-block px-4 py-2 rounded-full text-sm font-medium bg-${getCategoryInfo(viewingProduct.category).color}-100 text-${getCategoryInfo(viewingProduct.category).color}-700`}>
                    {getCategoryInfo(viewingProduct.category).label}
                  </span>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">TEMPS/CONSEIL</h4>
                  <p className="text-lg text-gray-900">{viewingProduct.cookingTimeOrAdvice}</p>
                </div>
              </div>

              {/* Boutons d'action */}
              <div className="flex gap-3 pt-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    openModal(viewingProduct);
                  }}
                  className="flex-1 flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Pencil className="w-5 h-5" />
                  Modifier
                </button>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setViewingProduct(null);
                  }}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsAdmin;
